import { Link } from "react-router-dom";
import ScrollReveal from "../components/marketing/ScrollReveal";
import { ArrowRight, Shield, Leaf, Zap, Lock, AlertTriangle, TrendingDown, CheckCircle, ArrowUpRight } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import PageHead from "../components/PageHead";

export default function HomePage() {
  const { t } = useLang();

  return (
    <div data-testid="home-page">
      <PageHead title={null} description="KYRA active les donnees dormantes des distributeurs pour la securite alimentaire et la reduction du gaspillage. Plug & play, zero friction." />

      {/* ===== HERO ===== */}
      <section className="min-h-screen flex items-center relative overflow-hidden" style={{ padding: "100px 5.5% 72px" }}>
        <div className="absolute pointer-events-none select-none" style={{ right: "-3%", bottom: "-14%", fontFamily: "'Playfair Display'", fontSize: "min(38vw, 520px)", fontWeight: 900, color: "rgba(34,197,94,0.04)", lineHeight: 0.8, letterSpacing: -10 }}>KYRA</div>
        <div className="relative max-w-[860px]">
          <ScrollReveal>
            <div className="flex gap-1.5 flex-wrap mb-6">
              {[t("home.heroTag1"), t("home.heroTag2"), t("home.heroTag3"), t("home.heroTag4")].map((tag, i) => (
                <span key={i} className="text-[10px] font-medium uppercase tracking-[3px] px-2.5 py-1 rounded-md" style={{ background: i < 2 ? "rgba(34,197,94,0.08)" : "rgba(0,0,0,0.03)", border: `1px solid ${i < 2 ? "rgba(34,197,94,0.2)" : "rgba(0,0,0,0.06)"}`, color: i < 2 ? "var(--kyra-gm)" : "var(--kyra-mu)" }}>{tag}</span>
              ))}
            </div>
          </ScrollReveal>
          <ScrollReveal>
            <h1 className="font-black leading-[1.04] mb-7" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(38px, 5.5vw, 70px)", letterSpacing: -2.5, color: "var(--kyra-tx)" }}>
              {t("home.h1_1")}<br />
              <em style={{ color: "var(--kyra-gr)" }}>{t("home.h1_2")}</em><br />
              <span style={{ color: "var(--kyra-teal)", fontStyle: "normal" }}>{t("home.h1_3")}</span>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay="d1">
            <p className="text-[17px] font-light leading-[1.78] mb-8 max-w-[620px]" style={{ color: "var(--kyra-mu)" }}>{t("home.heroP")}</p>
          </ScrollReveal>
          <ScrollReveal delay="d1">
            <div className="flex flex-wrap gap-2 mb-10">
              {(t("home.heroPills") || []).map((pill, i) => (
                <span key={i} className="flex items-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-full" style={{ background: "rgba(34,197,94,0.06)", color: "var(--kyra-gm)", border: "1px solid rgba(34,197,94,0.15)" }}>
                  <CheckCircle className="w-3 h-3" /> {pill}
                </span>
              ))}
            </div>
          </ScrollReveal>
          <ScrollReveal delay="d2">
            <div className="flex gap-3 flex-wrap items-center mb-14">
              <Link to="/contact" data-testid="hero-cta-contact" className="no-underline text-white text-[13.5px] font-medium px-7 py-3.5 rounded-md transition-colors" style={{ background: "var(--kyra-gr)" }}>{t("home.ctaContact")}</Link>
              <Link to="/solutions" data-testid="hero-cta-solutions" className="text-[13px] no-underline transition-colors" style={{ color: "var(--kyra-mu)", borderBottom: "1px solid rgba(0,0,0,0.15)", paddingBottom: 1 }}>{t("home.ctaSolution")} <ArrowRight className="inline w-3 h-3 ml-1" /></Link>
            </div>
          </ScrollReveal>
          <ScrollReveal delay="d3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-0" style={{ borderTop: "1px solid var(--kyra-li)", paddingTop: 32 }}>
              {[
                { v: t("home.heroKpi1V"), l: t("home.heroKpi1L"), c: "var(--kyra-gr)" },
                { v: t("home.heroKpi2V"), l: t("home.heroKpi2L"), c: "var(--kyra-gm)" },
                { v: t("home.heroKpi3V"), l: t("home.heroKpi3L"), c: "var(--kyra-teal)" },
                { v: t("home.heroKpi4V"), l: t("home.heroKpi4L"), c: "var(--kyra-am)" },
              ].map((kpi, i) => (
                <div key={i} className="flex flex-col pr-6" style={{ paddingLeft: i > 0 ? 24 : 0, borderLeft: i > 0 ? "1px solid var(--kyra-li)" : "none" }}>
                  <span className="font-black leading-none tracking-tight" style={{ fontFamily: "'Playfair Display'", fontSize: 32, letterSpacing: -1, color: kpi.c }}>{kpi.v}</span>
                  <span className="text-[11px] mt-1.5 leading-snug font-light" style={{ color: "var(--kyra-mu)" }}>{kpi.l}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ===== SECTION 2 — THE PROBLEM ===== */}
      <section style={{ background: "var(--kyra-bg2)", padding: "96px 5.5%" }}>
        <div className="mb-14">
          <ScrollReveal><div className="flex items-center gap-2.5 mb-4 text-[10px] font-medium uppercase tracking-[4px]" style={{ color: "var(--kyra-teal)" }}><span className="w-5 h-px" style={{ background: "var(--kyra-teal)" }} /> {t("home.probLabel")}</div></ScrollReveal>
          <ScrollReveal><h2 className="font-black leading-[1.1] mb-5 max-w-[720px]" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(28px, 3.8vw, 46px)", letterSpacing: -1.5, color: "var(--kyra-tx)" }}>{t("home.probTitle")}</h2></ScrollReveal>
          <ScrollReveal delay="d1"><p className="text-base font-light leading-[1.78] max-w-[660px]" style={{ color: "var(--kyra-mu)" }}>{t("home.probSub")}</p></ScrollReveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {(t("home.probCards") || []).map((card, i) => {
            const colors = ["var(--kyra-gr)", "var(--kyra-teal)", "var(--kyra-am)", "var(--kyra-gm)"];
            const bgs = ["rgba(34,197,94,0.08)", "rgba(94,168,138,0.08)", "rgba(94,168,138,0.08)", "rgba(22,163,74,0.08)"];
            return (
              <ScrollReveal key={i} delay={i > 0 ? `d${Math.min(i, 3)}` : ""}>
                <div className="p-6 rounded-xl border h-full" style={{ background: "#fff", borderColor: "var(--kyra-li)" }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-4" style={{ background: bgs[i], color: colors[i] }}>
                    {[<AlertTriangle className="w-4 h-4" />, <Shield className="w-4 h-4" />, <Zap className="w-4 h-4" />, <TrendingDown className="w-4 h-4" />][i]}
                  </div>
                  <h3 className="text-sm font-semibold mb-2" style={{ color: "var(--kyra-tx)" }}>{card.title}</h3>
                  <p className="text-[12.5px] font-light leading-relaxed" style={{ color: "var(--kyra-mu)" }}>{card.desc}</p>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <ScrollReveal delay="d2">
          <div className="px-6 py-5 rounded-xl mb-8" style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.15)" }}>
            <p className="text-[14px] font-medium leading-relaxed" style={{ color: "var(--kyra-gm)" }}>{t("home.probInsight")}</p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay="d3">
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start px-6 py-6 rounded-xl border" style={{ background: "#fff", borderColor: "var(--kyra-li)" }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(94,168,138,0.1)" }}>
              <Lock className="w-5 h-5" style={{ color: "var(--kyra-teal)" }} />
            </div>
            <div>
              <h3 className="text-base font-semibold mb-1.5" style={{ color: "var(--kyra-tx)" }}>{t("home.probRegTitle")}</h3>
              <p className="text-[13px] font-light leading-relaxed" style={{ color: "var(--kyra-mu)" }}>{t("home.probRegText")}</p>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ===== SECTION 3 — KYRA SOLUTION ===== */}
      <section style={{ padding: "96px 5.5%", background: "#fff" }}>
        <div className="mb-14">
          <ScrollReveal><div className="flex items-center gap-2.5 mb-4 text-[10px] font-medium uppercase tracking-[4px]" style={{ color: "var(--kyra-gr)" }}><span className="w-5 h-px" style={{ background: "var(--kyra-gr)" }} /> {t("home.solLabel")}</div></ScrollReveal>
          <ScrollReveal><h2 className="font-black leading-[1.1] mb-5 max-w-[700px]" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(28px, 3.8vw, 46px)", letterSpacing: -1.5 }}>{t("home.solTitle")}</h2></ScrollReveal>
          <ScrollReveal delay="d1"><p className="text-base font-light leading-[1.78] max-w-[640px]" style={{ color: "var(--kyra-mu)" }}>{t("home.solSub")}</p></ScrollReveal>
        </div>

        <ScrollReveal delay="d1">
          <div className="flex flex-wrap gap-2 mb-10">
            {(t("home.solPills") || []).map((pill, i) => (
              <span key={i} className="flex items-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-full" style={{ background: "rgba(34,197,94,0.06)", color: "var(--kyra-gm)", border: "1px solid rgba(34,197,94,0.15)" }}>
                <CheckCircle className="w-3 h-3" /> {pill}
              </span>
            ))}
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {[
            { icon: <Zap className="w-5 h-5" />, ti: t("home.solF1T"), d: t("home.solF1D"), bg: "#4ADE80" },
            { icon: <TrendingDown className="w-5 h-5" />, ti: t("home.solF2T"), d: t("home.solF2D"), bg: "var(--kyra-lime)" },
            { icon: <Shield className="w-5 h-5" />, ti: t("home.solF3T"), d: t("home.solF3D"), bg: "var(--kyra-teal)" },
            { icon: <ArrowUpRight className="w-5 h-5" />, ti: t("home.solF4T"), d: t("home.solF4D"), bg: "#4ADE80" },
          ].map((f, i) => {
            const isLight = i === 1;
            return (
              <ScrollReveal key={i} delay={i > 1 ? "d1" : ""}>
                <div className="p-7 rounded-xl h-full" style={{ background: f.bg, color: isLight ? "var(--kyra-tx)" : "#fff" }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: isLight ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.2)" }}>{f.icon}</div>
                  <h3 className="text-[15px] font-bold mb-2 tracking-tight" style={{ fontFamily: "'Playfair Display'" }}>{f.ti}</h3>
                  <p className="text-[13px] font-light leading-relaxed" style={{ opacity: isLight ? 0.7 : 0.8 }}>{f.d}</p>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <ScrollReveal delay="d2">
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start p-7 rounded-xl text-white" style={{ background: "var(--kyra-gm)" }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.15)" }}>
              <Lock className="w-5 h-5" style={{ color: "#fff" }} />
            </div>
            <div>
              <h3 className="text-base font-semibold mb-1.5">{t("home.solRegTitle")}</h3>
              <p className="text-[13px] font-light leading-relaxed" style={{ opacity: 0.7 }}>{t("home.solRegText")}</p>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay="d3">
          <div className="mt-8 px-6 py-5 rounded-xl" style={{ background: "var(--kyra-bg2)", border: "1px solid var(--kyra-li)" }}>
            <p className="text-[14px] font-normal leading-relaxed" style={{ color: "var(--kyra-tx)" }}>{t("home.solNoComplex")}</p>
          </div>
        </ScrollReveal>
      </section>

      {/* ===== FOOD SAFETY ===== */}
      <section style={{ background: "var(--kyra-teal)", color: "#fff", padding: "96px 5.5%" }}>
        <div className="mb-14">
          <ScrollReveal><div className="flex items-center gap-2.5 mb-4 text-[10px] font-medium uppercase tracking-[4px]" style={{ color: "rgba(255,255,255,0.6)" }}><span className="w-5 h-px" style={{ background: "currentcolor" }} /> {t("home.fsLabel")}</div></ScrollReveal>
          <ScrollReveal><h2 className="font-black leading-[1.1] mb-5 text-white max-w-[620px]" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(28px, 3.8vw, 46px)", letterSpacing: -1.5 }}>{t("home.fsTitle")}</h2></ScrollReveal>
          <ScrollReveal delay="d1"><p className="text-base font-light leading-[1.78] max-w-[580px]" style={{ color: "rgba(255,255,255,0.6)" }}>{t("home.fsSub")}</p></ScrollReveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {(t("home.fsItems") || []).map((item, i) => (
            <ScrollReveal key={i} delay={i > 1 ? "d1" : ""}>
              <div className="p-6 rounded-xl h-full" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <div className="flex items-center gap-3 mb-3"><div className="w-1 h-8 rounded-full" style={{ background: "rgba(255,255,255,0.4)" }} /><h3 className="text-sm font-semibold text-white">{item.title}</h3></div>
                <p className="text-[12.5px] font-light leading-relaxed pl-4" style={{ color: "rgba(255,255,255,0.55)" }}>{item.desc}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
        <ScrollReveal delay="d2">
          <div className="grid grid-cols-3 gap-4">
            {[{ v: t("home.fsKpi1V"), l: t("home.fsKpi1L") }, { v: t("home.fsKpi2V"), l: t("home.fsKpi2L") }, { v: t("home.fsKpi3V"), l: t("home.fsKpi3L") }].map((kpi, i) => (
              <div key={i} className="text-center p-5 rounded-xl" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Playfair Display'", color: "#fff" }}>{kpi.v}</div>
                <div className="text-[11px] mt-1 font-light" style={{ color: "rgba(255,255,255,0.45)" }}>{kpi.l}</div>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </section>

      {/* ===== ANTI-WASTE ===== */}
      <section style={{ background: "#4ADE80", color: "#111", padding: "96px 5.5%" }}>
        <div className="mb-14">
          <ScrollReveal><div className="flex items-center gap-2.5 mb-4 text-[10px] font-medium uppercase tracking-[4px]" style={{ color: "rgba(0,0,0,0.45)" }}><span className="w-5 h-px" style={{ background: "currentcolor" }} /> {t("home.fwLabel")}</div></ScrollReveal>
          <ScrollReveal><h2 className="font-black leading-[1.1] mb-5 max-w-[600px]" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(28px, 3.8vw, 46px)", letterSpacing: -1.5, color: "var(--kyra-tx)" }}>{t("home.fwTitle")}</h2></ScrollReveal>
          <ScrollReveal delay="d1"><p className="text-base font-light leading-[1.78] max-w-[580px]" style={{ color: "rgba(0,0,0,0.55)" }}>{t("home.fwSub")}</p></ScrollReveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {(t("home.fwItems") || []).map((item, i) => (
            <ScrollReveal key={i} delay={i > 1 ? "d1" : ""}>
              <div className="p-6 rounded-xl h-full" style={{ background: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.6)" }}>
                <div className="flex items-center gap-3 mb-3"><div className="w-1 h-8 rounded-full" style={{ background: "var(--kyra-gm)" }} /><h3 className="text-sm font-semibold" style={{ color: "var(--kyra-tx)" }}>{item.title}</h3></div>
                <p className="text-[12.5px] font-light leading-relaxed pl-4" style={{ color: "rgba(0,0,0,0.55)" }}>{item.desc}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
        <ScrollReveal delay="d2">
          <div className="grid grid-cols-3 gap-4">
            {[{ v: t("home.fwKpi1V"), l: t("home.fwKpi1L") }, { v: t("home.fwKpi2V"), l: t("home.fwKpi2L") }, { v: t("home.fwKpi3V"), l: t("home.fwKpi3L") }].map((kpi, i) => (
              <div key={i} className="text-center p-5 rounded-xl" style={{ background: "rgba(255,255,255,0.5)" }}>
                <div className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Playfair Display'", color: "var(--kyra-gm)" }}>{kpi.v}</div>
                <div className="text-[11px] mt-1 font-light" style={{ color: "rgba(0,0,0,0.45)" }}>{kpi.l}</div>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative overflow-hidden text-center" style={{ background: "var(--kyra-dk)", color: "#fff", padding: "88px 5.5%" }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] pointer-events-none" style={{ background: "radial-gradient(ellipse, rgba(34,197,94,0.15) 0%, transparent 70%)" }} />
        <div className="relative z-10 max-w-[560px] mx-auto">
          <ScrollReveal><div className="flex items-center gap-2.5 mb-4 text-[10px] font-medium uppercase tracking-[4px] justify-center" style={{ color: "rgba(74,222,128,0.5)" }}><span className="w-5 h-px" style={{ background: "currentcolor" }} /> {t("home.ctaLabel")}</div></ScrollReveal>
          <ScrollReveal><h2 className="font-black leading-[1.1] mb-3 text-white" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(28px, 3.8vw, 46px)" }}>{t("home.ctaTitle")}</h2></ScrollReveal>
          <ScrollReveal delay="d1"><p className="text-[15px] font-light max-w-[440px] mx-auto mb-8" style={{ color: "rgba(255,255,255,0.4)" }}>{t("home.ctaSub")}</p></ScrollReveal>
          <ScrollReveal delay="d2">
            <div className="flex gap-2.5 justify-center flex-wrap">
              <Link to="/contact" data-testid="cta-contact" className="no-underline px-7 py-3.5 rounded-md text-[13px] font-medium transition-opacity hover:opacity-90 text-white" style={{ background: "var(--kyra-gr)" }}>{t("home.ctaBtn1")}</Link>
              <Link to="/solutions" className="no-underline px-7 py-3.5 rounded-md text-[13px] transition-all hover:text-white" style={{ border: "1px solid rgba(255,255,255,0.16)", color: "rgba(255,255,255,0.5)" }}>{t("home.ctaBtn2")}</Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
